// Funciones de utilidad general

import { CaseStatus, UserRole, PHONE_CODES, MODALITY_NAMES } from './constants';

/**
 * Calcula edad actual desde fecha de nacimiento.
 * Soporta `DD/MM/YYYY` y `YYYYMMDD`.
 * Devuelve `undefined` si fecha no es válida.
 *
 * @param {string} birthDate
 * @returns {number|undefined}
 */
export function calculateAgeFromBirthDate(birthDate) {
  if (typeof birthDate !== 'string') return undefined;

  const value = birthDate.trim();
  if (!value) return undefined;

  let day;
  let month;
  let year;

  if (value.includes('/')) {
    [day, month, year] = value.split('/').map(Number);
  } else if (/^\d{8}$/.test(value)) {
    year = Number(value.slice(0, 4));
    month = Number(value.slice(4, 6));
    day = Number(value.slice(6, 8));
  } else {
    return undefined;
  }

  if (![day, month, year].every(Number.isFinite)) return undefined;

  const birth = new Date(year, month - 1, day);
  if (Number.isNaN(birth.getTime())) return undefined;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

/**
 * Devuelve un valor monetario formateado: 100000 -> 100.000,00
 * @param {number | string} value
 */
export function currencyFormat(value) {
  return Number(value).toLocaleString('es', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Devuelve el valor legible del sexo (Masculino o Femenino)
 * @param {"M" | "F"} sex
 */
export function readableSex(sex) {
  if (sex === 'M') {
    return 'Masculino';
  }
  return 'Femenino';
}

/**
 * Devuelve la fecha en formato yyyy/mm/dd
 * @param {Date} date
 */
export function formatDate(date) {
  // Validate
  if (!(date instanceof Date)) {
    return date;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
}

/**
 * Returns a date from yyyy-mm-dd to dd/mm/yyyy
 * @param {string} date
 */
export function formatDateString(date) {
  // Validate
  if (!date || typeof date !== 'string') {
    return date;
  }

  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

/**
 * Devuelve el nombre del banco a partir de su ID
 * @param {number | string} id
 * @param {Array | undefined} banks
 */
/**
 * maskAccount — Enmascara un número de cuenta mostrando solo los
 * últimos 4 dígitos (formato `****1234`). Usado para mostrar la
 * cuenta bancaria del titular en el detalle del reembolso sin
 * exponer el número completo al staff que NO debe verlo.
 *
 * Si el número tiene menos de 4 dígitos, retorna el número tal cual
 * (no se puede enmascarar de forma segura).
 */
export function maskAccount(accountNumber) {
  if (!accountNumber || typeof accountNumber !== 'string') return '—';
  if (accountNumber.length < 4) return accountNumber;
  return `****${accountNumber.slice(-4)}`;
}

export function getBankNameFromId(id, banks) {
  const bank = banks?.find(bank => Number(bank.id) === Number(id));
  return bank?.name ?? '';
}

/**
 * Devuelve una cédula formateada: V12345678 -> V-12345678
 * @param {string} ci
 */
export function formatCi(ci) {
  // Validate
  if (!ci || typeof ci !== 'string') {
    return ci;
  }

  return ci.replace(/^([VE])-?/, '$1-');
}

/**
 * Returns phone number formatted: 584243001234 -> 0424-3001234
 * @param {string} phoneNumber
 */
export function formatFullNumber(phoneNumber) {
  // Validate
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return phoneNumber;
  }

  return phoneNumber.replace(/^58/, '0').replace(/(\d{4})(\d{7})/, '$1-$2');
}

/**
 * Formatea segundos a mm:ss
 * @param {number} seconds
 */
export function formatTimeFromSeconds(seconds) {
  const total = Number.isFinite(seconds) ? seconds : 0;
  const mins = Math.floor(total / 60);
  const secs = Math.floor(total % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Returns the token from localStorage
 * @returns {string|null}
 */
export function getAuthToken() {
  return localStorage.getItem('token');
}
/**
 * Returns HTTP header part for authorization
 */
export function getAuthHeader() {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Token ${token}`,
  };
}

/**
 * Helper function to handle fetch requests
 * @param {string} url
 * @param {object} options
 * @returns {Promise<any>}
 */
export async function fetchData(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeader(),
      ...options.headers,
    },
  });
  if (!response.ok) {
    throw new Error(JSON.stringify(await response.json()));
  }
  return response.json();
}

/**
 * Get available options to change medical case status for current user role
 */
export function getStatusChangeOptionsForRole(currentStatus) {
  const role = localStorage.getItem('role');

  if (role === UserRole.MEDICAL_CENTER) {
    if (currentStatus === CaseStatus.PENDIENTE) return [CaseStatus.PENDIENTE, CaseStatus.ATENDIDO];
    if (currentStatus === CaseStatus.ATENDIDO) return [CaseStatus.ATENDIDO, CaseStatus.REMESADO];
    return [CaseStatus.ATENDIDO, CaseStatus.REMESADO, CaseStatus.PENDIENTE];
  }
  if (role === UserRole.MASTER) {
    return Object.values(CaseStatus);
  }

  return [];
}

/**
 * Returns the difference in days between a date and the current
 * @param {Date} date
 */
export function dateDiffFromNow(date) {
  const currentDate = new Date();

  // Calculate the difference in time (in milliseconds)
  const timeDifference = currentDate - date;

  // Convert the difference from milliseconds to days
  return Math.floor(timeDifference / (1000 * 60 * 60 * 24));
}

export function truncateText(text, maxLength) {
  if (!text) return '';
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
}

/**
 * Funcion para normalizar texto: minusculas, sin acentos, sin espacios extra
 * @param {string} text
 * @returns {string}
 */
export const normalizeText = text => {
  return (text || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
};

/**
 * Handle phone number paste event to automatically fill country code and number fields
 * @param {ClipboardEvent} e
 * @param {string} codeField
 * @param {string} numberField
 * @param {object} form
 */
export function handlePhonePaste(e) {
  const text = e.clipboardData.getData('text/plain');
  const newText = text.replace(/\D/g, '');
  let code = '';
  let phoneNumber = '';

  if (newText.length === 11) {
    code = newText.slice(0, 4);
    phoneNumber = newText.slice(4);
  } else if (newText.length === 10) {
    code = '0' + newText.slice(0, 3);
    phoneNumber = newText.slice(3);
  } else if (newText.length === 12) {
    code = '0' + newText.slice(2, 5);
    phoneNumber = newText.slice(5);
  }

  e.preventDefault();
  // using findPhoneCode
  const phoneCode = findPhoneCode(code);
  if (phoneCode) {
    code = phoneCode.value;
  } else {
    code = '0412'; // Default code if not found
  }

  return { code, phoneNumber };
}

/**
 * Find phone code by value or label
 * @param {string} phoneCode
 */
export function findPhoneCode(phoneCode) {
  return PHONE_CODES.find(item => {
    return item.value === phoneCode || item.label === phoneCode;
  });
}

/**
 * Get formatted chat timestamp
 * @param {string} timestamp
 */
export function getChatTimestamp(timestamp) {
  if (!timestamp) return '';

  const date = new Date(timestamp);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) {
    // Show time: hh:mm
    return date.toLocaleTimeString('es-VE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } else if (msgDate.getTime() === yesterday.getTime()) {
    return 'Ayer';
  } else {
    // dd/mm/yy
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }
}

/**
 * Person search item template
 *
 * Renderiza cada item del dropdown con padding uniforme (sin divider ni
 * py-1 adicional). El wrapper `<li class="p-autocomplete-item">` de
 * PrimeReact ya aporta padding + hover nativo, así que solo necesitamos
 * el texto + el handler de click.
 *
 * Antes se incluía `<Divider />` entre items, pero ese Divider traía
 * 1rem de margen top/bottom por default de PrimeReact, lo que hacía
 * que los items se vieran muy separados (32px extra entre cada uno).
 * El legacy CreateCase.css tenía `margin: 1rem 0` para compensarlo,
 * regla que se perdió en el refactor a CreateCaseV3.
 *
 * @param {(item: import("./MedicalCases/CreateCase").API_Person) => void} onClick
 */
export function createSearchItemTemplate(onClick) {
  /** @param {import("./MedicalCases/CreateCase").API_Person} item */
  return item => (
    <span onClick={() => onClick(item)}>
      {item.ci} - {item.name}
    </span>
  );
}

/**
 * Devuelve el nombre legible de la modalidad de estudio
 * @param {string} modality
 * @returns {string}
 */
export function getModalityName(modality) {
  return MODALITY_NAMES[modality] || modality || '-';
}

/**
 * Formatea la fecha YYYYMMDD a DD/MM/YYYY
 * @param {string} dateStr
 * @returns {string}

export function formatMedicalStudyDate(dateStr) {
  if (!dateStr || dateStr.length !== 8) return dateStr || '-';
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  return `${day}/${month}/${year}`;
}
 */

export function formatMedicalStudyDate(dateStr) {
  if (!dateStr) return '-';

  // Si es formato ISO (2025-11-12T16:05:22.538Z)
  if (dateStr.includes('T')) {
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateStr;
    }
  }

  // Si es formato yyyyMMdd (20250922)
  if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${day}/${month}/${year}`;
  }

  // Si ya está en formato dd/mm/yyyy, dejarlo igual
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  // Para cualquier otro caso, devolver original
  return dateStr;
}

/**
 * Formatea la hora de un estudio DICOM (tag StudyTime, formato HHMMSS o
 * HHMMSS.ffffff) a HH:MM:SS. Si el valor no es un HHMMSS válido, se
 * devuelve tal cual (o '-' si está vacío).
 */
export function formatDicomTime(timeStr) {
  if (!timeStr) return '-';

  const digitsOnly = String(timeStr).split('.')[0];
  if (/^\d{6}$/.test(digitsOnly)) {
    const hours = digitsOnly.substring(0, 2);
    const minutes = digitsOnly.substring(2, 4);
    const seconds = digitsOnly.substring(4, 6);
    return `${hours}:${minutes}:${seconds}`;
  }

  return timeStr;
}

/**
 * Convierte una fecha en varios formatos a un objeto Date.
 * Soporta:
 * - ISO: "2025-11-12T16:05:22.538Z"
 * - yyyy-mm-dd: "2025-01-16"
 * - DICOM: "20250922"
 * - dd/mm/yyyy: "12/11/2025"
 */
export function parseMedicalStudyDate(dateStr) {
  if (!dateStr) return null;

  // Caso 1: ISO (con 'T')
  if (dateStr.includes('T')) {
    try {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }

  // Caso 2: yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  // Caso 3: DICOM (yyyyMMdd)
  if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1; // Meses en JS: 0 = enero
    const day = parseInt(dateStr.substring(6, 8), 10);
    const date = new Date(year, month, day); // ⚠️ ¡Corregido aquí!
    return isNaN(date.getTime()) ? null : date;
  }

  // Caso 4: dd/mm/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
}

/**
 * Calcula cuántos días quedan desde la fecha del estudio (máx. 90).
 * @param {string} studyDateStr - Fecha en cualquier formato soportado
 * @returns {number} Días restantes (0 a 90)
 */
export function calculateRemainingDays(studyDateStr) {
  const studyDate = parseMedicalStudyDate(studyDateStr);
  if (!studyDate) return 90;

  const today = new Date();
  // Normalizamos ambas fechas a medianoche para comparar solo días
  const studyTime = new Date(
    studyDate.getFullYear(),
    studyDate.getMonth(),
    studyDate.getDate()
  ).getTime();
  const todayTime = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

  const diffInDays = Math.floor((todayTime - studyTime) / (1000 * 60 * 60 * 24));
  return Math.max(0, 90 - diffInDays);
}

/**
 * Retorna true si el usuario es admin según localStorage local
 */
export function isAdmin() {
  return localStorage.getItem('isAdmin') === 'true';
  // cambio
}
